import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import { useStores } from '~/hooks/useStores'
import { MyBtn } from '~/components/ui/MyBtn'

import styles from './UserModal.module.css'
import classNames from 'classnames'
import { useMemo } from 'react'
import Cookies from 'js-cookie'
import { useAuth } from '~/hooks/useAuth'
import { useMutation } from '@apollo/client'
import { LOGOUT } from '~/graphqls/gqls'
import { logout } from '~/graphqls/types'
import { useReload } from '~/hooks/useReload'
import { digitalUnit } from '~/utils'
import { MyIcon } from '~/components/ui/MyIcon'
import { AiEquity } from '~/graphqls/types'
import { CDNImage } from '~/components/business/CDNImage'
import { MyLink } from '~/components/ui/MyLink'

interface Props {
  // visible: boolean
  close: () => void
}

export const UserModal = observer(({ close }: Props) => {
  const { store } = useStores()
  const { reload } = useReload()
  const { isCreator, isAiCreator } = useAuth()
  const [logoutAction] = useMutation<logout>(LOGOUT)

  //最高等级会员
  const aiEquity = useMemo(() => {
    return store.aiEquity?.[store.aiEquity?.length - 1] || null
  }, [store.aiEquity])

  const level = useMemo(() => {
    return AiEquity?.filter(it => it.type === aiEquity?.cardType)?.[0]?.level
  }, [aiEquity])

  const onLogout = () => {
    logoutAction()
    Cookies.remove('gate-token')
    close()
    reload()
  }

  let quickMenu = [
    {
      icon: '/images/icons/publish.png',
      label: '我的主页',
      href: `/user/${store.userinfo?.base?.uid}`,
      exist: true,
    },
    {
      icon: '/images/icons/icon-creator-center@3x.png',
      label: '创作者中心',
      href: '/creator-center',
      exist: true,
    },
    {
      icon: '/images/icons/like.png',
      label: '赞和收藏',
      href: `/my/like`,
      exist: true,
    },
    {
      icon: '/images/icons/integral.png',
      label: '我的积分',
      href: `/integral`,
      exist: true,
    },
  ]

  return (
    <div
      className={classNames(
        'flex flex-col w-[400px] bg-neutral-100 px-4 py-8 relative',
        styles.modal
      )}
    >
      <div className="absolute top-4 right-4" onClick={close}>
        <MyIcon
          icon="close"
          className="text-neutral-400 text-[20px] hover:text-primary cursor-pointer transition-all"
        />
      </div>

      <div className="flex items-center">
        <Link
          href={`/user/${store.userinfo?.base?.uid}`}
          className="text-lg font-semibold text-neutral-800 mr-2 max-w-[160px] line-clamp-1"
        >
          {store.userinfo?.nickname}
        </Link>
        {aiEquity?.cardType ? (
          <MyLink
            href={`/ai/equity-card?type=${aiEquity?.cardType}`}
            exist={true}
          >
            <div className={classNames(styles.equity, [`equity-l${level}`])}>
              {level === 0 ? (
                <div className="flex items-center text-neutral-400">
                  <MyIcon icon="level-origin" className="text-[20px] mr-1" />
                  <span>{aiEquity?.name}</span>
                </div>
              ) : (
                <>
                  <span className={styles.equityIcon}>
                    <CDNImage
                      className="w-full h-full"
                      src={`/images/ai/equity/${aiEquity?.cardType}.svg`}
                    />
                  </span>
                  <span>{aiEquity?.name}</span>
                  <span>
                    L
                    {
                      AiEquity?.filter(
                        it => it.type === aiEquity?.cardType
                      )?.[0]?.level
                    }
                  </span>
                </>
              )}
            </div>
          </MyLink>
        ) : (
          <MyLink href={`/ai/equity-card`} exist={true}>
            <MyBtn
              type="primary"
              size="small"
              className="!h-8 mr-3 !text-primary !bg-[#E4DCFB] !border-[#F4F5F6] !font-semibold"
            >
              开通 AI 创作权益卡
            </MyBtn>
          </MyLink>
        )}

        <MyLink href="/settings/base" exist={true}>
          <MyIcon
            icon="edit"
            className="text-neutral-400 text-base hover:text-primary"
          />
        </MyLink>
      </div>

      <div className="flex items-center text-xs mt-2">
        <Link href={`/user/${store.userinfo?.base?.uid}/following`}>
          <span className="text-neutral-400">关注 </span>
          <span className="text-neutral-400">
            {digitalUnit(store.userinfo?.social?.followCount)}
          </span>
        </Link>
        <Link
          href={`/user/${store.userinfo?.base?.uid}/follower`}
          className="ml-6"
        >
          <span className="text-neutral-400">粉丝 </span>
          <span className="text-neutral-400">
            {digitalUnit(store.userinfo?.social?.fansCount)}
          </span>
        </Link>
        {isCreator || isAiCreator ? (
          <span className="ml-6">
            <span className="text-neutral-400">获赞 </span>
            <span className="text-neutral-400">
              {digitalUnit(store.userinfo?.social?.likesCount)}
            </span>
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap justify-between items-center gap-3 mt-8">
        {quickMenu.map(item => {
          return (
            <MyLink
              key={item.label}
              className="text-center text-neutral-400 hover:text-neutral-600 block rounded-lg transition-all"
              href={item.href}
              exist={item.exist}
            >
              <CDNImage src={item.icon} className="w-10 block mx-auto" />
              <div className=" text-xs whitespace-nowrap">{item.label}</div>
            </MyLink>
          )
        })}
      </div>
      <div className="mt-6 text-xs flex">
        <MyLink
          className="flex items-center text-neutral-400 hover:text-neutral-600 transition-all"
          href="/my/invite"
          exist={true}
        >
          <MyIcon icon="sharesquare" className="mr-1" />
          邀请好友
        </MyLink>
        <MyLink
          className="flex items-center text-neutral-400 hover:text-neutral-600 transition-all ml-6"
          href="/settings/password"
          exist={true}
        >
          <MyIcon icon="user-1" className="mr-1" />
          密码设置
        </MyLink>
        <span
          onClick={onLogout}
          className="flex items-center text-neutral-400 hover:text-neutral-600 cursor-pointer transition-all ml-6"
        >
          <MyIcon icon="signout" className="mr-1" />
          退出登录
        </span>
      </div>
    </div>
  )
})
